import type { RecipientEntry } from "@/schemas/workflow/notification-node";
import type { VariableSelectionData } from "../components/variable-selector";

interface UseRecipientInputProps {
  elements: RecipientEntry[];
  onChange: (elements: RecipientEntry[]) => void;
}

/**
 * Hook personalizado para manejar la lógica de inputs de destinatarios (emails/teléfonos)
 * Proporciona funciones para actualizar, agregar, eliminar y cambiar tipo de elementos
 */
export const useRecipientInput = ({
  elements,
  onChange,
}: UseRecipientInputProps) => {
  /**
   * Actualiza el valor de un elemento en un índice específico
   * Para inputs directos (type="inp"), recibe un string
   * Para variables (type="var"), recibe VariableSelectionData con connection/field/global_type
   */
  const updateElement = (id: string, data: string | VariableSelectionData) => {
    const index = parseInt(id);
    const newElements = [...elements];
    if (typeof data === "string") {
      // Input directo: solo actualizar value, limpiar connection, field y global_type
      newElements[index] = {
        ...newElements[index],
        value: data,
        connection: undefined,
        field: undefined,
        global_type: undefined,
      };
    } else if (data.global_type) {
      // Variable global: usar value y global_type, sin connection ni field
      newElements[index] = {
        ...newElements[index],
        value: data.value,
        connection: undefined,
        field: undefined,
        global_type: data.global_type,
      };
    } else {
      // Variable normal: actualizar value, connection y field
      newElements[index] = {
        ...newElements[index],
        value: data.value,
        connection: data.connection,
        field: data?.field,
        output_name: data?.output_name,
        global_type: undefined,
      };
    }
    onChange(newElements);
  };

  /**
   * Elimina un elemento en un índice específico
   */
  const removeElement = (id: string) => {
    const index = parseInt(id);
    const newElements = elements.filter((_, i) => i !== index);
    onChange(newElements);
  };

  /**
   * Agrega un nuevo elemento con tipo 'inp' y valor vacío
   */
  const addElement = () => {
    onChange([...elements, { type: "inp", value: "" }]);
  };

  /**
   * Alterna el tipo de un elemento entre 'inp' y 'var'
   */
  const toggleElementType = (id: string) => {
    const index = parseInt(id);
    const newElements = [...elements];
    const currentType = newElements[index].type;
    newElements[index] = {
      type: currentType === "inp" ? "var" : "inp",
      value: "",
      // Limpiar connection, field y global_type al cambiar de tipo
      connection: undefined,
      field: undefined,
      global_type: undefined,
    };
    onChange(newElements);
  };

  /**
   * Verifica si se pueden agregar más elementos
   * Solo permite agregar si todos los elementos actuales tienen valor
   */
  const canAddMoreElements = elements.every((el) => el.value !== "");

  return {
    updateElement,
    removeElement,
    addElement,
    toggleElementType,
    canAddMoreElements,
  };
};
